<template>
   <div>
      <navbar-component />
      <main class="bg-blueGray-100">
         <!-- Hero -->
         <section
            class="relative overflow-hidden pt-16 py-20 md:py-28"
            :style="{ backgroundColor: '#111827' }"
         >
            <div
               class="absolute inset-0 bg-center bg-cover"
               :style="{ backgroundImage: `url(${heroImage})`, opacity: 0.4 }"
            ></div>
            <div
               class="absolute inset-0"
               :style="{ backgroundColor: 'rgba(0, 0, 0, 0.55)' }"
            ></div>
            <div class="container relative mx-auto px-4 md:px-6">
               <div class="max-w-3xl">
                  <span
                     class="inline-block bg-blue-600 text-white text-xs font-bold tracking-wider uppercase px-3 py-1 rounded-full mb-5 shadow-sm"
                  >
                     Since 1989 · Calgary
                  </span>
                  <h1
                     class="text-white font-bold text-4xl md:text-5xl lg:text-6xl leading-tight mb-5"
                  >
                     About AMI
                  </h1>
                  <p
                     class="text-blueGray-100 text-lg md:text-xl leading-relaxed max-w-2xl mb-4"
                  >
                     Decades of frac controls experience—applied to brand-new
                     equipment for today’s spread.
                  </p>
                  <p
                     class="text-blueGray-300 text-base md:text-lg leading-relaxed max-w-2xl mb-8"
                  >
                     We still stand behind the installed base. We also design,
                     build, and ship the next generation of AMI controls.
                  </p>
                  <button
                     type="button"
                     @click="scrollToContact"
                     class="inline-block bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200"
                  >
                     Contact AMI
                     <i class="fas fa-arrow-right ml-2"></i>
                  </button>
               </div>
            </div>
         </section>

         <!-- Today’s focus -->
         <section class="py-16 md:py-20 bg-blueGray-800">
            <div class="container mx-auto px-4 md:px-6">
               <div class="max-w-3xl mx-auto text-center mb-12">
                  <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
                     Building new frac controls today
                  </h2>
                  <p class="text-lg text-blueGray-300 leading-relaxed">
                     Many operators know AMI for legacy systems that have run
                     reliably for years. That history still matters—but AMI has
                     changed. Our focus is modern equipment and fleet control,
                     plus custom work when a customer needs something unique.
                  </p>
               </div>

               <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
                  <div
                     v-for="stat in stats"
                     :key="stat.label"
                     class="bg-blueGray-900 rounded-xl p-6 border border-blueGray-700 text-center"
                  >
                     <p class="text-3xl font-bold text-white mb-1">
                        {{ stat.value }}
                     </p>
                     <p class="text-blueGray-400 text-sm">{{ stat.label }}</p>
                  </div>
               </div>
            </div>
         </section>

         <!-- What we ship -->
         <section class="py-16 md:py-20 bg-blueGray-900">
            <div class="container mx-auto px-4 md:px-6">
               <div class="text-center max-w-3xl mx-auto mb-10 md:mb-12">
                  <h2 class="text-3xl md:text-4xl font-bold text-white mb-3">
                     What we’re shipping now
                  </h2>
                  <p class="text-lg text-blueGray-300">
                     Next-generation platforms on WaveEdge and WaveData Cloud—
                     with legacy DVCommand still supported for existing fleets.
                  </p>
               </div>

               <div class="grid md:grid-cols-2 gap-5 max-w-5xl mx-auto mb-10">
                  <router-link
                     v-for="item in products"
                     :key="item.to"
                     :to="item.to"
                     class="bg-blueGray-800 rounded-xl p-6 border border-blueGray-700 hover:border-blueGray-500 transition-colors duration-200 group"
                  >
                     <div class="flex items-start gap-4">
                        <div
                           class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border-2"
                           :class="item.iconWrapClass"
                        >
                           <i :class="item.icon"></i>
                        </div>
                        <div>
                           <h3
                              class="text-lg font-bold text-white mb-1 group-hover:text-lightBlue-300 transition-colors"
                           >
                              {{ item.title }}
                           </h3>
                           <p class="text-blueGray-400 text-sm leading-relaxed">
                              {{ item.text }}
                           </p>
                        </div>
                     </div>
                  </router-link>
               </div>

               <p
                  class="text-center text-blueGray-500 text-sm max-w-2xl mx-auto"
               >
                  WaveEdge powers acquisition, control, and connectivity at the
                  edge across these products.
               </p>
            </div>
         </section>

         <!-- Contact -->
         <section class="py-16 md:py-20 bg-blueGray-800">
            <div class="container mx-auto px-4 md:px-6">
               <div
                  class="bg-blueGray-900 rounded-2xl p-8 md:p-10 border border-blueGray-700 text-center max-w-3xl mx-auto"
               >
                  <h3 class="text-2xl font-bold text-white mb-4">
                     Talk with AMI
                  </h3>
                  <p class="text-blueGray-300 mb-6">
                     Calgary, Alberta · (403) 463-1911
                  </p>
                  <button
                     type="button"
                     @click="scrollToContact"
                     class="inline-block bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200"
                  >
                     Contact AMI
                     <i class="fas fa-arrow-right ml-2"></i>
                  </button>
               </div>
            </div>
         </section>
      </main>
      <footer-component />
   </div>
</template>

<script>
import NavbarComponent from "@/components/Navbars/IndexNavbar.vue";
import FooterComponent from "@/components/Footers/Footer.vue";
import { setPageSeo } from "@/utils/seo";
import heroImage from "@/assets/img/industrial-engineer-programming-machine-control.jpg";

export default {
   name: "About",
   components: {
      NavbarComponent,
      FooterComponent,
   },
   data() {
      return {
         heroImage,
         stats: [
            { value: "1989", label: "Established in Calgary" },
            { value: "25+", label: "Years in frac controls" },
            { value: "2,500+", label: "Control systems deployed" },
            { value: "1,500+", label: "Frac pumps supported" },
         ],
         products: [
            {
               title: "WaveCommand7",
               to: "/wavecommand7",
               text: "Next-gen data van control—treatment plans, fleet views, and richer alarms.",
               icon: "fas fa-layer-group",
               iconWrapClass:
                  "bg-blue-900 text-lightBlue-300 border-lightBlue-400",
            },
            {
               title: "PumpLink7",
               to: "/pumplink7",
               text: "Next-generation pump control hardware for the frac spread.",
               icon: "fas fa-cogs",
               iconWrapClass: "bg-indigo-900 text-indigo-300 border-indigo-400",
            },
            {
               title: "BlenderLink7",
               to: "/blenderlink7",
               text: "Flexible sand, additive, and treatment-plan control on WaveEdge.",
               icon: "fas fa-blender",
               iconWrapClass:
                  "bg-emerald-900 text-emerald-300 border-emerald-500",
            },
            {
               title: "Datavan MPC",
               to: "/mpc",
               text: "Multi-pump control from Pump Down through full fleet operations.",
               icon: "fas fa-sliders-h",
               iconWrapClass: "bg-amber-900 text-amber-300 border-amber-400",
            },
            {
               title: "WaveData Cloud",
               to: "/wavedata",
               text: "Remote monitoring, alarms, and operational visibility across sites.",
               icon: "fas fa-cloud",
               iconWrapClass: "bg-cyan-900 text-cyan-300 border-cyan-400",
            },
            {
               title: "DVCommand suite",
               to: "/dvcommand",
               text: "Proven legacy frac control—still supported for existing fleets.",
               icon: "fas fa-truck",
               iconWrapClass:
                  "bg-blueGray-700 text-blueGray-300 border-blueGray-500",
            },
         ],
      };
   },
   mounted() {
      setPageSeo({
         title: "About AMI | Advanced Measurements",
         description:
            "Advanced Measurements Inc. builds next-generation frac equipment controls—WaveCommand7, PumpLink7, BlenderLink7, Datavan MPC, and WaveData Cloud—backed by decades of field experience from Calgary.",
         keywords:
            "AMI, Advanced Measurements, about AMI, frac controls, WaveEdge, WaveCommand7, Calgary",
      });
   },
   methods: {
      scrollToContact() {
         this.$router.push("/").then(() => {
            this.$nextTick(() => {
               const contactSection = document.getElementById("contact");
               if (contactSection) {
                  contactSection.scrollIntoView({ behavior: "smooth" });
               }
            });
         });
      },
   },
};
</script>
